<template>
  <div
    class="carousel-container-mobile"
    @touchstart.passive="touching = true"
    @touchend="touching = false"
    @touchcancel="touching = false"
    @focusin="onFocusIn"
    @focusout="focused = false"
  >
    <div class="img-part">
      <ClientOnly>
        <Carousel
          ref="imageCarousel"
          v-bind="imageCarouselProps"
          @slide-start="(data) => syncSliders2(data as SlideStartEvent)"
          @slide-end="(data) => resetLeft(data as SlideEndEvent)"
        >
          <Slide
            v-for="(slide, index) in sliderItems"
            :key="slide.title"
            class="slide-content"
          >
            <NuxtLink
              :to="slide.ctaRoute"
              class="slide-link"
            >
              <div style="height: 100%">
                <NuxtImg
                  :src="slide.image"
                  :alt="slide.title"
                  :style="{
                    transform: `translate3d(${imgsLeft[index]}%, 0, 0)`,
                  }"
                  sizes="xs:100vw sm:100vw md:100vw lg:100vw xl:100vw"
                  format="auto"
                  quality="80"
                  loading="lazy"
                />
                <div
                  v-if="slide.insertText"
                  class="text-img"
                >
                  <p
                    v-if="slide.insertDetail"
                    class="small"
                  >
                    {{ slide.insertDetail }}
                  </p>
                  <p class="big">{{ slide.insertText }}</p>
                </div>
              </div>
            </NuxtLink>
          </Slide>
        </Carousel>
      </ClientOnly>
    </div>
    <div class="text-part">
      <ClientOnly>
        <Carousel
          ref="textCarousel"
          v-bind="textCarouselProps"
          @slide-start="(data) => syncSliders1(data as SlideStartEvent)"
        >
          <Slide
            v-for="slide in sliderItems"
            :key="slide.title"
            class="slide-content"
          >
            <NuxtLink
              :to="slide.ctaRoute"
              class="slide-link"
            >
              <div class="inner-slide">
                <p class="sup-title">{{ slide.typeTitle }}</p>
                <h2>{{ capitalize(slide.title) }}</h2>
                <p
                  v-if="slide.sliderDescription"
                  class="description"
                >
                  <span>{{ slide.sliderDescription }}</span>
                </p>
                <ButtonSimple
                  :label="slide.ctaLabel"
                  color="white"
                />
              </div>
            </NuxtLink>
          </Slide>
        </Carousel>
        <div
          v-if="sliderItems.length > 1"
          class="progress"
        >
          <span
            v-for="(slide, index) in sliderItems"
            :key="slide.title"
            class="segment"
          >
            <!-- the fill of the current slide runs for 5 s, then moves on -->
            <span
              v-if="index === currentIndex"
              :key="`fill-${progressKey}`"
              class="fill"
              :class="{ paused: touching || focused }"
              @animationend="showNext"
            />
          </span>
        </div>
      </ClientOnly>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  CarouselInstance,
  CarouselProps,
  FormattedDoNotMiss,
  SlideEndEvent,
  SlideStartEvent,
} from '@/types'
import { Carousel, Slide } from 'vue3-carousel'

const props = defineProps<{
  sliderItems: FormattedDoNotMiss[]
}>()

const imgsLeft = ref<number[]>([])

const textCarousel = ref<CarouselInstance>(null)
const imageCarousel = ref<CarouselInstance>(null)

// Autoplay: the progress fill of the current slide restarts on every slide
// and pauses while a finger is on the slider or it holds the keyboard focus
const currentIndex = ref(0)
const progressKey = ref(0)
const touching = ref(false)
const focused = ref(false)

// Only keyboard focus pauses: a clicked or tapped arrow keeps the focus,
// which would otherwise hold the autoplay until the next click elsewhere
const onFocusIn = (event: FocusEvent) => {
  focused.value = (event.target as HTMLElement).matches(':focus-visible')
}

const startProgress = (index: number) => {
  currentIndex.value = index
  progressKey.value++
}

// no wrapAround here: from the last slide, autoplay goes back to the first
const showNext = () => {
  textCarousel.value?.slideTo(
    (currentIndex.value + 1) % props.sliderItems.length,
  )
}

const imageCarouselProps = {
  transition: 800,
} satisfies CarouselProps

const textCarouselProps = {
  transition: 800,
} satisfies CarouselProps

// vue3-carousel emits slide-start before it marks itself as sliding: each carousel following the other would
// call the other's slideTo again and again (stack overflow, track frozen mid-swipe on phones). The carousel
// that starts a slide moves the other one once; the nested slide-start this causes does not echo back.
// Neither carousel wraps around: the other one always goes to the same index (next() from the last slide did
// nothing, so swiping back from the 2nd of 2 slides left the image on the 2nd).
let syncing = false
const follow = (move: () => void) => {
  if (syncing) return
  syncing = true
  try {
    move()
  } finally {
    syncing = false
  }
}

const syncSliders1 = ({ slidingToIndex }: SlideStartEvent) => {
  startProgress(slidingToIndex)
  follow(() => imageCarousel.value?.slideTo(slidingToIndex))
}

const syncSliders2 = ({
  currentSlideIndex,
  slidingToIndex,
}: SlideStartEvent) => {
  startProgress(slidingToIndex)
  imgsLeft.value.splice(slidingToIndex, 1, 0)
  imgsLeft.value.splice(currentSlideIndex, 1, 100)

  follow(() => textCarousel.value?.slideTo(slidingToIndex))
}

const resetLeft = ({ currentSlideIndex }: SlideEndEvent) => {
  if (currentSlideIndex > 0) {
    imgsLeft.value.splice(currentSlideIndex - 1, 1, 0)
  }

  if (currentSlideIndex < imgsLeft.value.length - 1) {
    imgsLeft.value.splice(currentSlideIndex + 1, 1, 0)
  }
}
onMounted(async () => {
  await nextTick()

  imgsLeft.value = new Array(props.sliderItems.length).fill(0)
})
</script>

<style lang="scss">
.carousel-container-mobile {
  width: 100%;
  height: 100%;
  background: $blue;

  .carousel__viewport,
  .carousel__track,
  .carousel {
    height: 100%;
  }

  .carousel__track,
  .carousel__slide {
    display: flex !important;
    height: 100%;
  }

  .carousel__slide > div {
    flex-grow: 1;
  }

  .carousel__slide {
    &:not(.carousel__slide--active) {
      pointer-events: none;
    }
  }

  .slide-link {
    display: block;
    height: 100%;
    color: inherit;
    text-decoration: none;
  }

  .text-part {
    position: relative;
    width: 100%;
    height: 100%;

    .carousel__slide {
      align-self: stretch;
      height: auto;
    }

    .carousel__track {
      padding-bottom: 4.5rem !important;
    }

    .progress {
      position: absolute;
      right: 2.5rem;
      bottom: 2rem;
      left: 2.5rem;
      display: flex;
      gap: 0.6rem;
    }

    .segment {
      flex: 1 1 0;
      height: 0.3rem;
      overflow: hidden;
      background: rgb(255 255 255 / 0.25);
    }

    .fill {
      display: block;
      width: 100%;
      height: 100%;
      background: $white;
      transform-origin: left;
      animation: do-not-miss-mobile-progress 5s linear forwards;

      &.paused {
        animation-play-state: paused;
      }

      // no autoplay: the current slide is simply filled
      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    }

    .slick-dots {
      bottom: 2rem;
      left: 50%;
      width: auto;
      transform: translateX(-50%);

      li {
        margin: 0;
      }

      li button::before {
        color: $white;
        font-size: 0.4rem;
      }

      li.slick-active button::before {
        font-size: 0.9rem;
        opacity: 1;
      }
    }

    .slide-content {
      width: 100%;
      height: 100%;
      padding-top: 2.5rem;

      .inner-slide {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        width: 100%;
        height: 100%;
        padding: 0 2.5rem;
        text-align: left;
      }

      .sup-title {
        margin-bottom: 1rem;
        color: $white;
        font-size: 1.4rem;
        font-weight: 500;
        line-height: 1.8rem;
        letter-spacing: 0.14rem;
        text-transform: uppercase;
        opacity: 0.65;
      }

      h2 {
        display: -webkit-box;
        height: 8.2rem;
        margin-bottom: 2rem;
        overflow: hidden;
        color: $white;
        font-family: SST;
        font-size: 3.2rem;
        font-weight: 400;
        line-height: 4.1rem;
        letter-spacing: 0;
        text-transform: none;
        text-overflow: ellipsis;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2; /* number of lines to show */
        line-clamp: 2;
      }

      .description {
        margin-bottom: 2rem;
        color: $white;
        font-size: 1.4rem;
        white-space: pre-line;
      }

      .button {
        margin-top: auto;
      }
    }
  }

  .img-part {
    width: 100%;
    height: 66vw;

    .slide-content {
      position: relative;
      width: 100%;
      height: 100%;

      .slide-link {
        display: block;
      }
    }

    img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      backface-visibility: hidden;
      transition: transform 0.8s ease;
      pointer-events: none;
      object-fit: cover;
      object-position: bottom right;
    }

    .text-img {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 20.6rem;
      padding: 1rem 2rem;
      color: $blue;
      font-family: 'SST Condensed';
      text-transform: uppercase;
      background: $white;

      .small {
        font-size: 1rem;
        line-height: 1.3rem;
      }

      .big {
        font-size: 1.6rem;
        font-weight: bold;
        line-height: 2.1rem;
      }
    }
  }
}

@keyframes do-not-miss-mobile-progress {
  from {
    transform: scaleX(0);
  }

  to {
    transform: scaleX(1);
  }
}
</style>
